// src/components/DevRoleSwitcher.js
// Only renders in development. Never ships to production.
// Overrides userProfile.role in AuthContext without touching Firestore.
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import styles from './RoleSwitcher.module.css';

const ROLE_META = {
  free:        { label: 'Free',        color: '#6b757e', icon: '◌' },
  team_member: { label: 'Team Member', color: '#4ECDC4', icon: '◎' },
  team_leader: { label: 'Team Leader', color: '#5f7942', icon: '⬡' },
  consultant:  { label: 'Consultant',  color: '#cba43a', icon: '◐' },
  admin:       { label: 'Admin',       color: '#922b3f', icon: '⊕' },
};

export default function DevRoleSwitcher() {
  const { userProfile, devRole, setDevRole, realRole, DEV_ROLES, stopImpersonation } = useAuth();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);

  // Only render in dev and when logged in
  if (!setDevRole || !userProfile) return null;

  const activeRole = devRole || realRole || 'free';
  const meta = ROLE_META[activeRole] || ROLE_META.free;

  const handleSwitch = (role) => {
    stopImpersonation();
    setDevRole(role === realRole ? null : role);
    navigate('/dashboard');
    setExpanded(false);
  };

  const handleReset = () => {
    stopImpersonation();
    setDevRole(null);
    navigate('/dashboard');
    setExpanded(false);
  };

  return (
    <div className={styles.wrapper}>

      {/* Expanded panel */}
      {expanded && (
        <div className={styles.panel}>
          <div className={styles.panelHeader}>Dev · Switch Role</div>

          <div className={styles.roleList}>
            {DEV_ROLES.map(role => {
              const m = ROLE_META[role];
              const isActive = role === activeRole;
              const isReal = role === realRole;
              return (
                <button
                  key={role}
                  onClick={() => handleSwitch(role)}
                  className={styles.roleBtn}
                  style={{
                    background: isActive ? `${m.color}22` : 'rgba(255,255,255,0.03)',
                    border: `1.5px solid ${isActive ? m.color + '66' : 'rgba(255,255,255,0.07)'}`,
                  }}
                >
                  <span className={styles.roleIcon} style={{ color: m.color }}>{m.icon}</span>
                  <span className={styles.roleLabel} style={{ color: isActive ? 'white' : 'rgba(255,255,255,0.55)' }}>
                    {m.label}
                  </span>
                  {isReal && <span className={styles.realBadge}>real</span>}
                  {isActive && !isReal && (
                    <span className={styles.mockBadge} style={{ color: m.color, background: `${m.color}18` }}>mock</span>
                  )}
                </button>
              );
            })}
          </div>

          {devRole && (
            <button onClick={handleReset} className={styles.resetBtn}>
              ↩ Reset to real role
            </button>
          )}

          <div className={styles.panelFooter}>
            Dev only · not visible in production
          </div>
        </div>
      )}

      {/* Trigger pill */}
      <button
        onClick={() => setExpanded(e => !e)}
        title="Dev: Switch role"
        className={styles.triggerPill}
        style={{
          background: expanded ? '#1a1a2e' : 'rgba(20,20,40,0.92)',
          border: `1.5px solid ${devRole ? meta.color + '88' : 'rgba(255,255,255,0.15)'}`,
        }}
      >
        <div
          className={styles.dot}
          style={{
            background: meta.color,
            boxShadow: devRole ? `0 0 0 2px ${meta.color}44` : 'none',
            animation: devRole ? 'devPulse 2s ease-in-out infinite' : 'none',
          }}
        />
        <span className={styles.pillLabel}>{meta.label}</span>
        {devRole && (
          <span className={styles.pillMock} style={{ color: meta.color }}>mock</span>
        )}
        <span className={styles.pillChevron}>{expanded ? '▾' : '◂'}</span>
      </button>
    </div>
  );
}
